import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  Search, ShieldCheck, BadgeCheck, Wallet, Star, ArrowRight,
  Plane, ShoppingBag, HandCoins, PackageCheck, ChevronDown,
} from "lucide-react";
import { Logo } from "../components/Logo";
import { LangToggle, Rating } from "../components/common";
import api from "../lib/api";
import { rupiah } from "../lib/format";
import { useI18n } from "../lib/i18n";

const HERO = "https://images.unsplash.com/photo-1741835906792-eb13a54af7d9?crop=entropy&cs=srgb&fm=jpg&w=1200&q=80";

export default function Landing() {
  const { t } = useI18n();
  const navigate = useNavigate();
  const [dests, setDests] = useState([]);
  const [trips, setTrips] = useState([]);
  const [q, setQ] = useState("");
  const [faq, setFaq] = useState(null);

  useEffect(() => {
    api.get("/destinations").then((r) => setDests(r.data)).catch(() => {});
    api.get("/trips").then((r) => setTrips(r.data.slice(0, 6))).catch(() => {});
  }, []);

  const goSearch = () => navigate(`/explore${q ? `?q=${encodeURIComponent(q)}` : ""}`);

  const steps = {
    titiper: [
      { icon: Search, t: "Find someone going there" },
      { icon: ShoppingBag, t: "Send your request" },
      { icon: Wallet, t: "Pay securely" },
      { icon: PackageCheck, t: "Receive your item" },
    ],
    bawainer: [
      { icon: Plane, t: "Post your trip" },
      { icon: ShoppingBag, t: "Receive requests" },
      { icon: HandCoins, t: "Buy the item" },
      { icon: PackageCheck, t: "Bring it home & earn" },
    ],
  };

  const faqs = [
    ["Apa itu BAWAIN?", "BAWAIN menghubungkan orang yang mau titip barang dari luar kota/negara dengan traveler yang sedang ke sana."],
    ["Apakah pembayaran aman?", "Ya. Dana kamu ditahan secara escrow oleh BAWAIN dan hanya dilepas ke BAWAINER setelah kamu konfirmasi barang diterima."],
    ["Bagaimana kalau barang tidak sampai?", "Kamu bisa membuka dispute sebelum order selesai otomatis. Tim kami akan meninjau bukti & chat."],
    ["Berapa biaya platform?", "BAWAIN mengambil komisi 10% dari biaya jasa BAWAINER (bukan dari harga produk)."],
  ];

  return (
    <div className="bg-cloud">
      {/* Nav */}
      <header className="sticky top-0 z-40 glass border-b border-black/5">
        <div className="max-w-screen-xl mx-auto px-4 h-16 flex items-center justify-between">
          <Logo />
          <div className="flex items-center gap-2">
            <LangToggle />
            <Link to="/login" data-testid="nav-login" className="px-4 py-2 rounded-full text-sm font-semibold text-navy hover:bg-white">{t("login")}</Link>
            <Link to="/register" data-testid="nav-register" className="px-4 py-2 rounded-full text-sm font-bold bg-navy text-white hover:bg-navy-700">{t("register")}</Link>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="max-w-screen-xl mx-auto px-4 pt-10 pb-14 grid lg:grid-cols-2 gap-10 items-center">
          <div className="animate-fade-up">
            <span className="inline-flex items-center gap-2 bg-lime-light text-navy rounded-full px-3 py-1 text-xs font-bold">
              🌏 Titip barang antar kota & negara
            </span>
            <h1 className="font-heading font-black tracking-tighter text-4xl sm:text-5xl lg:text-6xl text-navy mt-4 leading-[1.05]">
              Mau dari sana?<br /><span className="text-lime-dark">Bawain.</span>
            </h1>
            <p className="text-base sm:text-lg text-muted-foreground mt-4 max-w-md">{t("hero_sub")}</p>

            <div className="mt-6 bg-white rounded-full shadow-soft p-1.5 flex items-center max-w-md">
              <Search className="ml-3 text-muted-foreground" size={18} />
              <input data-testid="hero-search" value={q} onChange={(e) => setQ(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && goSearch()}
                placeholder={t("search_ph")} className="flex-1 bg-transparent px-3 py-2 text-sm outline-none" />
              <button onClick={goSearch} data-testid="hero-search-btn"
                className="rounded-full bg-lime hover:bg-lime-dark text-navy font-bold px-5 py-2.5 text-sm transition-colors">Cari</button>
            </div>

            <div className="flex flex-wrap gap-3 mt-5">
              <Link to="/register" data-testid="hero-find-bawainer" className="rounded-full bg-navy text-white font-bold px-6 py-3 text-sm hover:bg-navy-700 transition-colors flex items-center gap-2">
                {t("find_bawainer")} <ArrowRight size={16} />
              </Link>
              <Link to="/register" data-testid="hero-post-trip" className="rounded-full bg-white border border-black/10 text-navy font-bold px-6 py-3 text-sm hover:bg-cloud transition-colors">
                {t("post_trip")}
              </Link>
            </div>
            <div className="flex items-center gap-4 mt-6 text-xs text-muted-foreground">
              <span className="flex items-center gap-1"><ShieldCheck size={15} className="text-sky" /> Secure escrow</span>
              <span className="flex items-center gap-1"><BadgeCheck size={15} className="text-sky" /> Verified travelers</span>
            </div>
          </div>
          <div className="relative animate-scale-in">
            <img src={HERO} alt="Traveler" className="rounded-3xl shadow-lift w-full h-[420px] object-cover" />
            <div className="absolute -bottom-4 -left-2 bg-white rounded-2xl shadow-lift p-3 flex items-center gap-2">
              <img src="https://i.pravatar.cc/80?img=12" className="w-9 h-9 rounded-full" alt="" />
              <div>
                <div className="text-xs font-bold text-navy flex items-center gap-1">Andi <BadgeCheck size={12} className="text-sky" /></div>
                <Rating value={4.9} size={11} />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Popular destinations */}
      <section className="max-w-screen-xl mx-auto px-4 py-10">
        <h2 className="font-heading font-extrabold text-2xl sm:text-3xl text-navy">Popular Destinations</h2>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-5">
          {dests.slice(0, 8).map((d) => (
            <Link key={d.code} to="/register" className="relative h-32 rounded-2xl overflow-hidden group shadow-soft" data-testid={`landing-dest-${d.code}`}>
              <img src={d.image} alt={d.name} className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" />
              <div className="absolute inset-0 bg-gradient-to-t from-navy/85 to-transparent" />
              <div className="absolute bottom-2 left-3 text-white">
                <div className="text-lg">{d.flag}</div>
                <div className="font-bold text-sm">{d.name}</div>
                <div className="text-lime text-[11px] font-semibold">{d.bawainer_count} BAWAINER</div>
              </div>
            </Link>
          ))}
        </div>
      </section>

      {/* How it works */}
      <section className="bg-navy text-white py-14">
        <div className="max-w-screen-xl mx-auto px-4">
          <h2 className="font-heading font-extrabold text-2xl sm:text-3xl">How BAWAIN Works</h2>
          <div className="grid md:grid-cols-2 gap-8 mt-8">
            {["titiper", "bawainer"].map((role) => (
              <div key={role}>
                <div className="inline-flex items-center gap-2 bg-white/10 rounded-full px-3 py-1 text-xs font-bold uppercase tracking-wide">
                  {role === "titiper" ? "For TITIPER" : "For BAWAINER"}
                </div>
                <div className="mt-4 space-y-3">
                  {steps[role].map((s, i) => {
                    const Icon = s.icon;
                    return (
                      <div key={i} className="flex items-center gap-3 bg-white/5 rounded-2xl p-3">
                        <span className="w-9 h-9 rounded-full bg-lime text-navy flex items-center justify-center font-bold">{i + 1}</span>
                        <Icon size={18} className="text-lime" />
                        <span className="text-sm font-medium">{s.t}</span>
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Trusted travelers */}
      <section className="max-w-screen-xl mx-auto px-4 py-12">
        <h2 className="font-heading font-extrabold text-2xl sm:text-3xl text-navy">Trusted BAWAINERs</h2>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 mt-5">
          {trips.map((tr) => (
            <div key={tr.id} className="bg-white rounded-3xl p-5 shadow-soft border border-black/5">
              <div className="flex items-center gap-3">
                <img src={tr.bawainer?.avatar} alt="" className="w-12 h-12 rounded-2xl object-cover" />
                <div>
                  <div className="font-bold text-navy flex items-center gap-1">{tr.bawainer?.name} <BadgeCheck size={13} className="text-sky" /></div>
                  <Rating value={tr.bawainer?.rating} count={tr.bawainer?.reviews_count} />
                </div>
              </div>
              <div className="mt-3 text-sm text-navy font-semibold">{tr.from_city} → {tr.to_city}</div>
              <div className="text-xs text-muted-foreground mt-1">{rupiah(tr.service_fee)}/item · {tr.capacity_kg}kg available</div>
            </div>
          ))}
        </div>
      </section>

      {/* Safety */}
      <section className="max-w-screen-xl mx-auto px-4 py-6">
        <div className="bg-lime-light/50 rounded-3xl p-8 grid md:grid-cols-3 gap-6">
          {[[ShieldCheck, "Secure Escrow Payment", "Funds held safely, released only after you confirm receipt."],
            [BadgeCheck, "Verified Travelers", "Identity verification & badges you can trust."],
            [Star, "Ratings & Reviews", "Real reviews from completed orders."]].map(([Icon, tt, d], i) => (
            <div key={i}>
              <Icon size={26} className="text-navy" />
              <h3 className="font-heading font-bold text-navy mt-2">{tt}</h3>
              <p className="text-sm text-muted-foreground mt-1">{d}</p>
            </div>
          ))}
        </div>
      </section>

      {/* FAQ */}
      <section className="max-w-2xl mx-auto px-4 py-12">
        <h2 className="font-heading font-extrabold text-2xl sm:text-3xl text-navy text-center">FAQ</h2>
        <div className="mt-6 space-y-2">
          {faqs.map(([q2, a], i) => (
            <div key={i} className="bg-white rounded-2xl border border-black/5">
              <button onClick={() => setFaq(faq === i ? null : i)} data-testid={`faq-${i}`}
                className="w-full flex items-center justify-between p-4 text-left font-semibold text-navy">
                {q2} <ChevronDown size={18} className={`transition-transform ${faq === i ? "rotate-180" : ""}`} />
              </button>
              {faq === i && <p className="px-4 pb-4 text-sm text-muted-foreground">{a}</p>}
            </div>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="bg-navy py-14">
        <div className="max-w-screen-xl mx-auto px-4 text-center">
          <h2 className="font-heading font-black text-3xl sm:text-4xl text-white">Find it. Request it. Bring it.</h2>
          <div className="flex flex-wrap gap-3 justify-center mt-6">
            <Link to="/register" className="rounded-full bg-lime text-navy font-bold px-7 py-3 hover:bg-lime-dark transition-colors">{t("find_bawainer")}</Link>
            <Link to="/register" className="rounded-full bg-white/10 text-white font-bold px-7 py-3 hover:bg-white/20 transition-colors">{t("become_bawainer")}</Link>
          </div>
        </div>
      </section>

      <footer className="bg-navy-900 text-white/60 py-8">
        <div className="max-w-screen-xl mx-auto px-4 flex flex-col sm:flex-row justify-between gap-4">
          <Logo light />
          <div className="flex flex-wrap gap-4 text-sm">
            <Link to="/safety" className="hover:text-lime">Safety</Link>
            <Link to="/legal/terms" className="hover:text-lime">Terms</Link>
            <Link to="/legal/privacy" className="hover:text-lime">Privacy</Link>
            <Link to="/legal/prohibited" className="hover:text-lime">Prohibited Items</Link>
            <Link to="/login" className="hover:text-lime">Contact</Link>
          </div>
        </div>
        <div className="max-w-screen-xl mx-auto px-4 mt-4 text-xs">© 2026 BAWAIN. All rights reserved.</div>
      </footer>
    </div>
  );
}
